/**
 * Displays a list of persons with actions
 *
 * @param {Object} props - component Props
 * @param {Array<Object>} props.tableData - list of persons in the table.
 * @param {string[]} [props.selectedIds] - selected person ids
 * @param {(e: React.ChangeEvent<HTMLInputElement>, tableData: Array<Object>) => void} [props.selectAllIds] - select all ids
 * @param {(e: React.ChangeEvent<HTMLInputElement>, item: Object) => void} [props.handleCheckbox] - handle individual selection
 * @param {(modalParams: Object) => void} [props.openModal] - function to open modals
 * @returns {JSX.Element}
 */

import Icon, { UserOutlined } from "@ant-design/icons";
import { Avatar, Button as AntButton, Card, Checkbox, Col, Flex, Row, Space, Typography } from "antd";

import { ReactComponent as MessageIcon } from "@assets/images/svg/message.svg";
import { ReactComponent as PhoneCall } from "@assets/images/svg/phoneCall.svg";

import { SEND_SMS_MODAL_HEADER } from "../../constants/labels";
import { getColor } from "../../helper";
import { CreateCandidatePageLink } from "utils/page-link-generator";

import CallModal from "@ui/candidate/call-modal";

import "./detail-person-search.scss";
import Button from "@ui/form-controls/button";

const { Text } = Typography;

function PersonList({ tableData, selectAllIds, handleCheckbox, selectedIds, openModal }) {
  return (
    <>
      {/* send message/email buttons */}
      <Row justify="end">
        <Space>
          <Button
            variant="primary"
            disabled={selectedIds.length === 0}
            onClick={() =>
              openModal({ modalName: "SendSmsAndAppMessageModal", personObjectIds: selectedIds })
            }
          >
            Send Message
          </Button>

          <Button
            variant="primary"
            disabled={selectedIds.length === 0}
            onClick={() =>
              openModal({
                modalName: "SendEmailByTemplateToPersonModal",
                personObjectIds: selectedIds
              })
            }
          >
            Send Email
          </Button>
        </Space>
      </Row>

      {/* select all checkbox */}
      <Flex gap="small">
        <Checkbox
          className="check-selected"
          indeterminate={selectedIds.length > 0 && selectedIds.length < tableData.length}
          checked={selectedIds.length === tableData.length && tableData.length > 0}
          onChange={e => selectAllIds(e, tableData)}
        />
        <Text className="font-bold" level={5}>
          Select All People ({`${selectedIds?.length || 0}`})
        </Text>
      </Flex>

      <Card className="person-list-card">
        {tableData?.map((item, idx) => (
          <Person
            key={idx}
            item={item}
            selectedIds={selectedIds}
            handleCheckbox={handleCheckbox}
            openModal={openModal}
          />
        ))}
      </Card>
    </>
  );
}

export default PersonList;

const Person = ({ item, selectedIds, handleCheckbox, openModal }) => {
  return (
    <Row justify="space-between" className="border-0 border-b border-gray px-1 py-1">
      <Col>
        <Space>
          <Avatar
            style={{
              backgroundColor: "green",
              verticalAlign: "middle"
            }}
            size="small"
          >
            <UserOutlined />
          </Avatar>

          <Checkbox
            className="check-selected"
            onChange={e => handleCheckbox(e, item)}
            checked={selectedIds.includes(item.ObjectId)}
          />

          <CreateCandidatePageLink
            mode="edit"
            personObjectId={item?.ObjectId}
            candidateObjectId={item?.CandidateList[0]?.ObjectId}
            candidateName={`${item?.Name}`}
            replace={true}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.Name}
          </CreateCandidatePageLink>

          {item?.Badges &&
            Array.isArray(item?.Badges) &&
            item?.Badges.map((data, idx) => (
              <AntButton
                key={idx}
                style={{ backgroundColor: getColor(data.BadgeType) }}
                className="cursor-default"
              >
                {data?.Text}
              </AntButton>
            ))}

          {item?.CandidateList[0] && item?.CandidateList[0]?.SiteOfInterest && (
            <AntButton style={{ backgroundColor: getColor("PAY_TIER") }} className="cursor-default">
              {item?.CandidateList[0]?.SiteOfInterest}
            </AntButton>
          )}
        </Space>
      </Col>

      <Col className="flex items-center gap-2">
        <CallModal personObjectId={item.ObjectId} candidateName={item.Name}>
          <Icon component={PhoneCall} className="cursor-pointer text-xl" />
        </CallModal>

        <Icon
          component={MessageIcon}
          className="cursor-pointer text-xl text-brand"
          onClick={() => {
            openModal({
              modalName: SEND_SMS_MODAL_HEADER,
              modalHeader: SEND_SMS_MODAL_HEADER,
              personObjectId: item?.ObjectId,
              candidateObjectId: item?.CandidateList[0]?.ObjectId,
              candidateName: item?.Name,
              width: "40%",
              type: "candidate"
            });
          }}
        />
      </Col>
    </Row>
  );
};
